// client/src/ApplicationsTable/JobModal.jsx
import React, { useState, useEffect } from "react";
import "./ApplicationsTable.css";
import "./JobModal.css";

const JobModal = (props) => {
  const {
    activeJob,
    isEditing,
    isNewJob,
    todayStr,
    formatToYMD,
    diffInDays,
    onClose,
    onStartEdit,
    onDelete,
    onSave,
    onCancel,
    onChangeField,
    onFileChange,
    onGenerateAI,
  } = props;

  const [jsonText, setJsonText] = useState("");
  const [contacts, setContacts] = useState([]);
  const [newContact, setNewContact] = useState({
    contact_date: todayStr,
    channel: "Email",
    direction: "outbound",
    summary: "",
    next_followup_date: "",
  });

  const API_BASE = "https://job.scae.co";

  useEffect(() => {
    if (!activeJob?.id) return;

    const fetchContacts = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/jobs/${activeJob.id}/contacts`);
        const data = await res.json();
        setContacts(data);
      } catch (err) {
        console.error("Failed to load contacts:", err);
      }
    };

    fetchContacts();
  }, [activeJob?.id]);

  const handleAddContact = async () => {
  if (!activeJob?.id) return;
  if (!newContact.summary.trim()) {
    alert("Write a short summary of the contact.");
    return;
  }

  try {
    const res = await fetch(`${API_BASE}/api/jobs/${activeJob.id}/contacts`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(newContact),
    });

    const saved = await res.json();
    if (!res.ok) {
      console.error("Contact save error:", saved);
      alert("Failed to save contact.");
      return;
    }

    // prepend contact to list
    setContacts((prev) => [saved, ...prev]);

    // update last / next follow-up in the modal fields
    if (saved.contact_date) {
      onChangeField("last_contact_date", saved.contact_date);
    }
    if (saved.next_followup_date) {
      onChangeField("next_followup_date", saved.next_followup_date);
    }

    // reset form
    setNewContact({
      contact_date: todayStr,
      channel: "Email",
      direction: "outbound",
      summary: "",
      next_followup_date: "",
    });
  } catch (err) {
    console.error("Contact add failed:", err);
    alert("Contact add failed.");
  }
  };


  if (!activeJob) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>
          ✕
        </button>

        <div className="modal-header">
          <div>
            <h2>{activeJob.company || "New Job"}</h2>
            <h3>{activeJob.job_title}</h3>
          </div>
        </div>

        <div className="modal-actions">
          {!isNewJob && !isEditing && (
            <>
              <button className="btn btn-primary" onClick={onStartEdit}>
                Edit
              </button>
            </>
          )}

          {(isEditing || isNewJob) && (
            <>
              <button className="btn btn-primary" onClick={onSave}>
                Save
              </button>
              <button className="btn btn-secondary" onClick={onCancel}>
                Cancel
              </button>
            </>
          )}
        </div>

        {/* LEFT + RIGHT */}
        <div className="modal-content">
          {/* LEFT: FORM GRID */}
          <div className="modal-main">
            <div className="modal-grid">
              {/* Company */}
              <div>
                <span className="label">Company</span>
                {isEditing || isNewJob ? (
                  <input
                    className="modal-input"
                    value={activeJob.company || ""}
                    onChange={(e) =>
                      onChangeField("company", e.target.value)
                    }
                  />
                ) : (
                  <span>{activeJob.company}</span>
                )}
              </div>

              {/* Job Title */}
              <div>
                <span className="label">Job Title</span>
                {isEditing || isNewJob ? (
                  <input
                    className="modal-input"
                    value={activeJob.job_title || ""}
                    onChange={(e) =>
                      onChangeField("job_title", e.target.value)
                    }
                  />
                ) : (
                  <span>{activeJob.job_title}</span>
                )}
              </div>

              {/* Job Page */}
              <div>
                <span className="label">Job Page</span>
                {isEditing || isNewJob ? (
                  <input
                    className="modal-input"
                    value={activeJob.job_page || ""}
                    onChange={(e) =>
                      onChangeField("job_page", e.target.value)
                    }
                  />
                ) : (
                    <a
                    href={activeJob.job_page}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open Posting
                  </a>
                )}
              </div>

              {/* Job Link */}
              <div>
                <span className="label">Job Link</span>
                {isEditing || isNewJob ? (
                  <input
                    className="modal-input"
                    value={activeJob.job_link || ""}
                    onChange={(e) =>
                      onChangeField("job_link", e.target.value)
                    }
                  />
                ) : activeJob.job_link ? (
                  <a
                    href={activeJob.job_link}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open Posting
                  </a>
                ) : (
                  <span>-</span>
                )}
              </div>

              {/* Location */}
              <div>
                <span className="label">Location</span>
                {isEditing || isNewJob ? (
                  <input
                    className="modal-input"
                    value={activeJob.location || ""}
                    onChange={(e) =>
                      onChangeField("location", e.target.value)
                    }
                  />
                ) : (
                  <span>{activeJob.location}</span>
                )}
              </div>

              {/* Salary */}
              <div>
                <span className="label">Salary</span>
                {isEditing || isNewJob ? (
                  <input
                    className="modal-input"
                    value={activeJob.salary || ""}
                    onChange={(e) =>
                      onChangeField("salary", e.target.value)
                    }
                  />
                ) : (
                  <span>{activeJob.salary}</span>
                )}
              </div>

              {/* Job Source */}
              <div>
                <span className="label">Job Source</span>
                {isEditing || isNewJob ? (
                  <input
                    className="modal-input"
                    value={activeJob.job_source || ""}
                    onChange={(e) =>
                      onChangeField("job_source", e.target.value)
                    }
                  />
                ) : (
                  <span>{activeJob.job_source || "-"}</span>
                )}
              </div>

              {/* Date Applied */}
              <div>
                <span className="label">Date Applied</span>
                <span>{formatToYMD(activeJob.date_applied) || "-"}</span>
              </div>

              {/* Days Since Applied */}
              <div>
                <span className="label">Days Since Applied</span>
                <span>
                  {activeJob.date_applied
                    ? diffInDays(activeJob.date_applied, todayStr)
                    : "-"}
                </span>
              </div>

              {/* Resume Version */}
              <div>
                <span className="label">Resume Version</span>
                {isEditing || isNewJob ? (
                  <input
                    className="modal-input"
                    value={activeJob.resume_version || ""}
                    onChange={(e) =>
                      onChangeField("resume_version", e.target.value)
                    }
                  />
                ) : (
                  <span>{activeJob.resume_version || "-"}</span>
                )}
              </div>

              {/* Cover Letter Used */}
              <div>
                <span className="label">Cover Letter Used</span>
                {isEditing || isNewJob ? (
                  <input
                    className="modal-input"
                    value={activeJob.cover_letter_used || ""}
                    onChange={(e) =>
                      onChangeField(
                        "cover_letter_used",
                        e.target.value
                      )
                    }
                  />
                ) : (
                  <span>{activeJob.cover_letter_used || "-"}</span>
                )}
              </div>

              {/* Recruiter Name */}
              <div>
                <span className="label">Recruiter Name</span>
                {isEditing || isNewJob ? (
                  <input
                    className="modal-input"
                    value={activeJob.recruiter_name || ""}
                    onChange={(e) =>
                      onChangeField("recruiter_name", e.target.value)
                    }
                  />
                ) : (
                  <span>{activeJob.recruiter_name || "-"}</span>
                )}
              </div>

              {/* Recruiter Email */}
              <div>
                <span className="label">Recruiter Email</span>
                {isEditing || isNewJob ? (
                  <input
                    className="modal-input"
                    value={activeJob.recruiter_email || ""}
                    onChange={(e) =>
                      onChangeField("recruiter_email", e.target.value)
                    }
                  />
                ) : (
                  <span>{activeJob.recruiter_email || "-"}</span>
                )}
              </div>

              {/* Last Contact */}
              <div>
                <span className="label">Last Contact Date</span>
                {isEditing || isNewJob ? (
                  <input
                    className="modal-input"
                    value={activeJob.last_contact_date || ""}
                    onChange={(e) =>
                      onChangeField(
                        "last_contact_date",
                        e.target.value
                      )
                    }
                  />
                ) : (
                  <span>
                    {formatToYMD(activeJob.last_contact_date) || "-"}
                  </span>
                )}
              </div>

              {/* Next Follow-up Date */}
              <div>
                <span className="label">Next Follow-up Date</span>
                {isEditing || isNewJob ? (
                  <input
                    className="modal-input"
                    value={activeJob.next_followup_date || ""}
                    onChange={(e) =>
                      onChangeField(
                        "next_followup_date",
                        e.target.value
                      )
                    }
                  />
                ) : (
                  <span>
                    {formatToYMD(activeJob.next_followup_date) || "-"}
                  </span>
                )}
              </div>

              {/* Days Until Follow-up */}
              <div>
                <span className="label">Days Until Follow-up</span>
                <span>
                  {activeJob.next_followup_date
                    ? diffInDays(todayStr, activeJob.next_followup_date)
                    : "-"}
                </span>
              </div>

              {/* Resume File */}
              <div>
                <span className="label">Resume File</span>
                {isEditing || isNewJob ? (
                  <>
                    <input
                      type="file"
                      onChange={(e) =>
                        onFileChange(
                          "resume_file_name",
                          e.target.files[0]
                        )
                      }
                    />
                    <div className="file-name">
                      {activeJob.resume_file_name || "No file selected"}
                    </div>
                  </>
                ) : (
                  <span>{activeJob.resume_file_name || "-"}</span>
                )}
              </div>

              {/* Cover Letter File */}
              <div>
                <span className="label">Cover Letter File</span>
                {isEditing || isNewJob ? (
                  <>
                    <input
                      type="file"
                      onChange={(e) =>
                        onFileChange(
                          "cover_letter_file_name",
                          e.target.files[0]
                        )
                      }
                    />
                    <div className="file-name">
                      {activeJob.cover_letter_file_name ||
                        "No file selected"}
                    </div>
                  </>
                ) : (
                  <span>{activeJob.cover_letter_file_name || "-"}</span>
                )}
              </div>

              {/* Notes */}
              <div className="modal-notes">
                <span className="label">Notes</span>
                {isEditing || isNewJob ? (
                  <textarea
                    className="modal-textarea"
                    value={activeJob.notes || ""}
                    onChange={(e) =>
                      onChangeField("notes", e.target.value)
                    }
                  />
                ) : (
                  <span>{activeJob.notes || "-"}</span>
                )}
              </div>
            </div>
              {/* CONTACT LOG */}
            <div className="modal-side-section">
              <h4>Recruiter Contact Log</h4>

              {/* Quick summary */}
              <div className="contact-summary">
                <span className="chips-title">Last Contact</span>
                <div className="contact-summary-row">
                  <span>
                    {formatToYMD(activeJob.last_contact_date) || "No contact yet"}
                  </span>
                </div>
              </div>

              {/* Add new contact */}
              <div className="contact-form">
                <div className="contact-form-row">
                  <label>
                    <span className="label">Date</span>
                    <input
                      type="date"
                      className="modal-input"
                      value={newContact.contact_date}
                      onChange={(e) =>
                        setNewContact((c) => ({ ...c, contact_date: e.target.value }))
                      }
                    />
                  </label>

                  <label>
                    <span className="label">Channel</span>
                    <select
                      className="modal-input"
                      value={newContact.channel}
                      onChange={(e) =>
                        setNewContact((c) => ({ ...c, channel: e.target.value }))
                      }
                    >
                      <option value="Email">Email</option>
                      <option value="Call">Call</option>
                      <option value="LinkedIn">LinkedIn</option>
                      <option value="In-person">In-person</option>
                      <option value="Other">Other</option>
                    </select>
                  </label>
                </div>

                <label>
                  <span className="label">Next Follow-up (optional)</span>
                  <input
                    type="date"
                    className="modal-input"
                    value={newContact.next_followup_date}
                    onChange={(e) =>
                      setNewContact((c) => ({
                        ...c,
                        next_followup_date: e.target.value,
                      }))
                    }
                  />
                </label>

                <label>
                  <span className="label">Summary</span>
                  <textarea
                    className="modal-textarea"
                    placeholder="E.g. Sent thank-you email after phone screen, waiting on update..."
                    value={newContact.summary}
                    onChange={(e) =>
                      setNewContact((c) => ({ ...c, summary: e.target.value }))
                    }
                  />
                </label>

                <button className="btn btn-primary contact-add-btn" onClick={handleAddContact}>
                  + Log Contact
                </button>
              </div>

              {/* Contact history list */}
              <div className="contact-history">
                <span className="chips-title">History</span>
                {contacts.length === 0 ? (
                  <div className="chips-empty">No contact history yet.</div>
                ) : (
                  <ul className="contact-list">
                    {contacts.map((c) => (
                      <li key={c.id} className="contact-item">
                        <div className="contact-item-main">
                          <div className="contact-item-header">
                            <span className="contact-date">
                              {formatToYMD(c.contact_date)}
                            </span>
                            <span className="contact-channel">{c.channel}</span>
                            {c.next_followup_date && (
                              <span className="contact-next">
                                Next: {formatToYMD(c.next_followup_date)}
                              </span>
                            )}
                          </div>
                          <div className="contact-summary-text">{c.summary}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {!isNewJob && !isEditing && (
            <>
              <button className="btn btn-danger" onClick={onDelete}>
                Delete
              </button>
            </>
          )}
          </div>

          {/* RIGHT SIDE: JSON + skills/tools/keywords + match */}
          <div className="modal-side">
            {/* JSON INPUT */}
            <div className="modal-side-section">
              <span className="label">AI JSON (paste here)</span>
              <textarea
                className="modal-textarea json-textarea"
                placeholder='Paste JSON like {"company": "...", "skills": [...]}'
                value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
              />

              <button
                className="btn btn-ai"
                onClick={() => onGenerateAI(jsonText)}
              >
                ⚡ Generate Skills from JSON
              </button>
            </div>

            {/* Skills / Tools / Keywords */}
            <div className="modal-side-section">
              <h4>Extracted Skills & Keywords</h4>

              <div className="chips-group">
                <span className="chips-title">Skills</span>
                <div className="chips-row">
                  {(activeJob.skills || []).length ? (
                    activeJob.skills.map((s, i) => (
                      <span key={`skill-${i}`} className="chip">
                        {s}
                      </span>
                    ))
                  ) : (
                    <span className="chips-empty">
                      No skills detected
                    </span>
                  )}
                </div>
              </div>

              <div className="chips-group">
                <span className="chips-title">Tools</span>
                <div className="chips-row">
                  {(activeJob.tools || []).length ? (
                    activeJob.tools.map((t, i) => (
                      <span key={`tool-${i}`} className="chip chip-tool">
                        {t}
                      </span>
                    ))
                  ) : (
                    <span className="chips-empty">
                      No tools detected
                    </span>
                  )}
                </div>
              </div>

              <div className="chips-group">
                <span className="chips-title">Keywords</span>
                <div className="chips-row">
                  {(activeJob.keywords || []).length ? (
                    activeJob.keywords.map((k, i) => (
                      <span
                        key={`kw-${i}`}
                        className="chip chip-keyword"
                      >
                        {k}
                      </span>
                    ))
                  ) : (
                    <span className="chips-empty">
                      No keywords detected
                    </span>
                  )}
                </div>
              </div>

              {/* Match score + missing + matched */}
              <div className="match-section">
                {activeJob.matchScore != null && (
                  <div className="match-score">
                    Match Score: <strong>{activeJob.matchScore}%</strong>
                  </div>
                )}

                {Array.isArray(activeJob.missing) &&
                  activeJob.missing.length > 0 && (
                    <div className="missing-section">
                      <span className="chips-title">
                        Missing / Gaps
                      </span>
                      <ul className="missing-list">
                        {activeJob.missing.map((m, i) => (
                          <li key={`missing-${i}`}>{m}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                {Array.isArray(activeJob.matched) &&
                  activeJob.matched.length > 0 && (
                    <div className="matched-section">
                      <span className="chips-title">Matched</span>
                      <ul className="matched-list">
                        {activeJob.matched.map((m, i) => (
                          <li key={`matched-${i}`}>{m}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {activeJob.matchScore != null && (<>
                  <div className="match-email">Email: </div>
                  <div className="match-email">{activeJob.email_massage}</div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobModal;
